import { Component } from 'preact';
import { motion } from 'framer-motion';
import AppHeader from '../AppHeader';
import { BackButton } from '../ui/navButtons';
import AmbientParticles from '../AmbientParticles';
import ModeTile from './ModeTile';
import {
  VIGNETTE, TEXT_PRIMARY, TEXT_MUTED,
} from '../../lib/medievalTheme';
import { getModesForVisibility, VISIBILITY_TILES } from '../../utils/arena/gameModes';
import { screenPanel } from '../../utils/screenEnter';
import { isPhoneLayout } from '../../utils/platform';
import { ChevronLeft } from 'lucide-react';
import { playUI, UI } from '../../utils/arena/uiSounds';
import { t } from '../../localization/t';

// Sub-screen shown after the user picks a visibility on
// SessionBrowser. Reuses the ModeTile grid from the old PlayScreen
// but filtered by visibility (Solo hides Draft/Sealed).
export default class ModePickerScreen extends Component {
  render() {
    const {
      visibility, profile, onBack, onPickMode, sorceryCards,
      // Social / mail / draft props forwarded straight to AppHeader so
      // the right-hand cluster (friends, music, skill tree, currencies)
      // stays consistent with every other top-level screen.
      onToggleMailbox, mailboxUnreadCount, mailboxOpen,
      onToggleFriends, friendListData, draftQueueDropdown,
    } = this.props;
    const vis = VISIBILITY_TILES.find((v) => v.key === visibility);
    const modes = getModesForVisibility(visibility);

    // Phone: back chip + title row, then the SAME ModeTile buttons in a
    // horizontally scrollable rem-sized row (2 tiles for Solo, 4 for
    // Friends/Public). vh-mode-tile chrome is re-specced in rem inside
    // the phone canvas; Tailwind text inside the tile resolves through
    // the phone tokens.
    if (isPhoneLayout()) {
      return (
        <div className="fixed inset-0 z-50 flex flex-col select-none" style={{ background: "url('/play-bg.webp') center/cover no-repeat, #08080a" }}>
          <div
            className="absolute inset-0 pointer-events-none"
            style={{ background: 'linear-gradient(180deg, rgba(0,0,0,0.75) 0%, rgba(0,0,0,0.45) 40%, rgba(0,0,0,0.5) 70%, rgba(0,0,0,0.85) 100%)' }}
          />
          <div
            className="relative z-10 flex items-center gap-3 pt-2 pb-1"
            style={{
              paddingLeft: 'max(0.75rem, env(safe-area-inset-left, 0px))',
              paddingRight: 'max(0.75rem, env(safe-area-inset-right, 0px))',
            }}
          >
            <button
              type="button"
              aria-label={t('Back')}
              className="flex items-center justify-center transition-transform duration-100 active:scale-[0.94]"
              style={{
                width: '2.75rem',
                height: '2.75rem',
                borderRadius: 'var(--radius-full)',
                background: 'linear-gradient(180deg, rgba(20,16,10,0.85) 0%, rgba(10,8,5,0.9) 100%)',
                border: '1px solid rgba(180, 140, 60, 0.28)',
                boxShadow: '0 2px 8px rgba(0,0,0,0.5)',
              }}
              onClick={() => { playUI(UI.CLOSE); onBack?.(); }}
            >
              <ChevronLeft size={22} color={TEXT_MUTED} />
            </button>
            <span
              className="arena-heading text-lg font-bold"
              style={{ color: TEXT_PRIMARY, textShadow: '0 1px 2px rgba(0,0,0,0.8)' }}
            >
              {t('Choose Game Mode')}
            </span>
            <span className="text-sm" style={{ color: TEXT_MUTED }}>
              {vis?.title ? t(vis.title) : visibility}
            </span>
          </div>
          <div
            className="relative z-10 flex-1 min-h-0 flex overflow-y-auto"
            style={{
              paddingLeft: 'max(0.75rem, env(safe-area-inset-left, 0px))',
              paddingRight: 'max(0.75rem, env(safe-area-inset-right, 0px))',
              paddingBottom: 'max(0.75rem, env(safe-area-inset-bottom, 0px))',
            }}
          >
            {/* Same two-column grid as desktop, scrolling vertically when
                the rows outgrow a short landscape viewport. */}
            <div
              className="grid gap-3 m-auto w-full"
              style={{ gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', maxWidth: '33rem' }}
            >
              {modes.map((mode, idx) => {
                const availability = mode.isAvailable?.(profile) || { available: true };
                return (
                  <ModeTile
                    key={mode.key}
                    mode={mode}
                    available={availability.available}
                    reason={availability.reason || null}
                    badges={mode.getBadges?.(profile) || []}
                    sorceryCards={sorceryCards}
                    ambient
                    ambientPhase={idx}
                    onClick={() => onPickMode?.(mode.key)}
                  />
                );
              })}
            </div>
          </div>
        </div>
      );
    }

    return (
      <div
        className="fixed inset-0 z-50 flex flex-col"
        style={{
          background: `url('/play-bg.webp') center/cover no-repeat, #08080a`,
                  }}
      >
        <div
          className="absolute inset-0"
          style={{ background: 'linear-gradient(180deg, rgba(0,0,0,0.75) 0%, rgba(0,0,0,0.45) 40%, rgba(0,0,0,0.5) 70%, rgba(0,0,0,0.85) 100%)' }}
        />
        <div className="absolute inset-0 pointer-events-none" style={{ background: VIGNETTE }} />
        <AmbientParticles preset="hub" />
        <AppHeader
          profile={profile}
          onToggleMailbox={onToggleMailbox}
          mailboxUnreadCount={mailboxUnreadCount}
          mailboxOpen={mailboxOpen}
          onToggleFriends={onToggleFriends}
          friendListData={friendListData}
          draftQueueDropdown={draftQueueDropdown}
          zoom={1}
        >
          <BackButton title={t('Back')} onClick={onBack} />
        </AppHeader>
        <div className="relative z-10 flex-1 min-h-0 overflow-y-auto">
          {/* Heading lives in a fixed-position band at the top of
              the content area; tile grid centers vertically in the
              remaining space. Keeps the 'Choose Game Mode' title
              and its subline at the same Y regardless of whether
              the grid below is 2 tiles (solo) or 4 tiles
              (friends / public). */}
          <div className="mx-auto max-w-[92.5cqw] w-full px-8 py-8 flex flex-col min-h-full">
            <motion.header className="text-center" {...screenPanel(1)}>
              <h1
                className="arena-heading text-4xl font-bold"
                style={{ color: TEXT_PRIMARY, textShadow: '0 2px 8px rgba(0,0,0,0.8)' }}
              >
                {t('Choose Game Mode')}
              </h1>
              <p
                className="text-sm mt-2"
                style={{ color: TEXT_MUTED }}
              >
                {vis?.title ? t(vis.title) : visibility}
              </p>
            </motion.header>
            <motion.div className="flex-1 flex items-center justify-center" {...screenPanel(2)}>
              {/* Fixed 2-column, 2-row grid. Each tile stays at
                  ~460 px wide (the size tiles had in the old
                  3-col PlayScreen). Missing tiles (e.g. Solo which
                  only has Match + Sandbox) are backfilled with
                  invisible placeholders of the same aspect ratio
                  so the grid always reserves space for four cells.
                  That way Match and Sandbox land at the same
                  coordinates regardless of visibility. */}
              <div
                className="grid gap-4"
                style={{ gridTemplateColumns: 'repeat(2, 28.75cqw)' }}
              >
                {modes.map((mode) => {
                  const availability = mode.isAvailable?.(profile) || { available: true };
                  return (
                    <ModeTile
                      key={mode.key}
                      mode={mode}
                      available={availability.available}
                      reason={availability.reason || null}
                      badges={mode.getBadges?.(profile) || []}
                      sorceryCards={sorceryCards}
                      onClick={() => onPickMode?.(mode.key)}
                    />
                  );
                })}
                {Array.from({ length: Math.max(0, 4 - modes.length) }).map((_, i) => (
                  <div
                    key={`mode-placeholder-${i}`}
                    className="aspect-[16/9] w-full invisible"
                    aria-hidden="true"
                  />
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    );
  }
}
